// ── Now Playing — the skin that takes its colour from the show ───────────────
// Every other skin is a fixed palette. This one reads the poster of whatever you
// are watching (the queue's top show on Home, the open show on a show page), pulls
// its dominant hue, and paints the app with it: accent, glow, and a deep tint of
// the poster's shadow tone under everything. Two people's apps never look the
// same, and yours changes when your show does. The breath animation lives in
// index.css (:root.theme-nowplaying); this file only decides the colours.
//
// Posters come through /.netlify/functions/img-proxy (TMDB sends no CORS header,
// so a direct draw would taint the canvas). Palettes are cached per poster path.
const CACHE_PREFIX = 'nextep_np_pal:'
const FALLBACK = { accent: '#f5c842', vivid: '#ffd966', bg: '#050408', raised: '#0c0a12', name: '' }

let base = null       // { posterPath, name } — the queue's show
let override = null   // { posterPath, name } — the show page you are on
let current = null    // last applied { ...palette, name }

const rgb2hsl = (r, g, b) => {
  r /= 255; g /= 255; b /= 255
  const max = Math.max(r, g, b), min = Math.min(r, g, b); const l = (max + min) / 2
  if (max === min) return [0, 0, l]
  const d = max - min; const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  let h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4
  return [h * 60, s, l]
}
const hsl2hex = (h, s, l) => {
  const f = n => { const k = (n + h / 30) % 12; const a = s * Math.min(l, 1 - l); const v = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1)); return Math.round(v * 255).toString(16).padStart(2, '0') }
  return `#${f(0)}${f(8)}${f(4)}`
}
const hexRgb = (hex) => { const n = parseInt(hex.slice(1), 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255] }

function derive(pixels) {
  // Dominant VIVID hue: 24 hue buckets weighted by saturation, ignoring near-black,
  // near-white and grey pixels (a poster's typography and gradients would otherwise win).
  const buckets = new Array(24).fill(0), sums = new Array(24).fill(0)
  let darkR = 0, darkG = 0, darkB = 0, darkN = 0
  for (let i = 0; i < pixels.length; i += 4) {
    const r = pixels[i], g = pixels[i + 1], b = pixels[i + 2]
    const [h, s, l] = rgb2hsl(r, g, b)
    if (l < 0.22) { darkR += r; darkG += g; darkB += b; darkN++ }
    if (l < 0.12 || l > 0.9 || s < 0.28) continue
    const k = Math.floor(h / 15) % 24
    const w = s * (1 - Math.abs(l - 0.5))
    buckets[k] += w; sums[k] += h * w
  }
  let best = 0; for (let k = 1; k < 24; k++) if (buckets[k] > buckets[best]) best = k
  const hue = buckets[best] > 0 ? sums[best] / buckets[best] : 45
  // Accent: fixed lightness so contrast is stable whatever the poster does; the
  // hue is the show's, the discipline is ours. Ink stays dark on it.
  const accent = hsl2hex(hue, 0.78, 0.62)
  const vivid  = hsl2hex(hue, 0.85, 0.74)
  // Ground: the poster's shadow tone, pulled down to near-black and desaturated so
  // text tokens (Dark's) keep their contrast — a tint you feel more than see.
  let bgH = hue, bgS = 0.22
  if (darkN > 0) { const [dh, ds] = rgb2hsl(darkR / darkN, darkG / darkN, darkB / darkN); if (ds > 0.08) { bgH = dh; bgS = Math.min(0.3, ds) } }
  const bg = hsl2hex(bgH, bgS, 0.035), raised = hsl2hex(bgH, bgS, 0.075)
  return { accent, vivid, bg, raised }
}

// Synchronous read of a palette this device has already derived — for a card that
// must paint in the show's colour on its FIRST frame (a swipe hand-off can't wait
// a promise tick without a visible flash). null when it has never been derived.
export function cachedPalette(posterPath) {
  if (!posterPath) return null
  try { const c = localStorage.getItem(CACHE_PREFIX + posterPath); return c ? JSON.parse(c) : null } catch { return null }
}

export function paletteFromPoster(posterPath) {
  if (!posterPath) return Promise.resolve(FALLBACK)
  try { const c = localStorage.getItem(CACHE_PREFIX + posterPath); if (c) return Promise.resolve(JSON.parse(c)) } catch {}
  return new Promise((resolve) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      try {
        const cv = document.createElement('canvas'); cv.width = 32; cv.height = 48
        const ctx = cv.getContext('2d', { willReadFrequently: true })
        ctx.drawImage(img, 0, 0, 32, 48)
        const pal = derive(ctx.getImageData(0, 0, 32, 48).data)
        try { localStorage.setItem(CACHE_PREFIX + posterPath, JSON.stringify(pal)) } catch {}
        resolve(pal)
      } catch { resolve(FALLBACK) }
    }
    img.onerror = () => resolve(FALLBACK)
    img.src = `/.netlify/functions/img-proxy?p=${encodeURIComponent(posterPath)}&s=w92`
  })
}

function isActive() {
  try { return document.documentElement.classList.contains('theme-nowplaying') } catch { return false }
}

export function applyPalette(pal, name = '') {
  const root = document.documentElement
  const [r, g, b] = hexRgb(pal.accent)
  const [br, bg, bb] = hexRgb(pal.bg)
  root.style.setProperty('--np-accent', pal.accent)
  root.style.setProperty('--np-vivid', pal.vivid)
  // The breathing --amber is composed in CSS from --np-accent/--np-vivid; an inline
  // --amber would out-rank that rule, so clear the static seed applyTheme() wrote.
  root.style.removeProperty('--amber')
  root.style.setProperty('--amber-rgb', `${r},${g},${b}`)
  root.style.setProperty('--amber-vivid', pal.vivid)
  root.style.setProperty('--amber-dim', `rgba(${r},${g},${b},0.75)`)
  root.style.setProperty('--amber-glow', `rgba(${r},${g},${b},0.22)`)
  root.style.setProperty('--amber-ink', '#07070f')
  root.style.setProperty('--bg', pal.bg)
  root.style.setProperty('--bg-rgb', `${br},${bg},${bb}`)
  root.style.setProperty('--bg-raised', pal.raised)
  root.style.setProperty('--nav-bg', `rgba(${br},${bg},${bb},0.97)`)
  root.style.setProperty('--scrim', `rgba(${br},${bg},${bb},0.78)`)
  root.style.setProperty('--sheet', `linear-gradient(160deg, ${pal.raised}, ${pal.bg})`)
  current = { ...pal, name }
  try { window.dispatchEvent(new CustomEvent('nextep:nowplaying', { detail: current })) } catch {}
}

let seq = 0
async function reapply() {
  if (!isActive()) return
  const src = override || base
  const my = ++seq
  const pal = await paletteFromPoster(src?.posterPath)
  if (my !== seq || !isActive()) return   // a newer show won, or the skin changed meanwhile
  applyPalette(pal, src?.name || '')
}

// Home / App: the queue's show. Show page: the show you are looking at (overrides).
export function setNowPlayingBase(show)     { base = show ? { posterPath: show.poster_path, name: show.name } : null; reapply() }
export function setNowPlayingOverride(show) { override = show ? { posterPath: show.poster_path, name: show.name } : null; reapply() }
export function clearNowPlayingOverride()   { override = null; reapply() }
export function getNowPlaying()             { return current }

// applyTheme() announces every skin change; when Now Playing comes on, paint it.
if (typeof window !== 'undefined') {
  window.addEventListener('nextep:theme-applied', (e) => { if (e.detail?.mode === 'nowplaying') reapply() })
}
